// src/pages/customers/tabs/ProductsTab.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  FiDownload,
  FiSearch,
  FiRefreshCw,
  FiArrowUp,
  FiArrowDown,
} from "react-icons/fi";
import API_BASE_URL from "../../../../utils/config";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const fmtDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (isNaN(dt)) return "-";
  return dt.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }); // e.g., 20 Sep 2025
};

// Compute current FY (India): Apr 1 – Mar 31
function getCurrentFYRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0=Jan
  const fyStartYear = m >= 3 ? y : y - 1; // Apr (3) or later -> same year else prev
  const start = new Date(fyStartYear, 3, 1); // Apr 1
  const end = new Date(fyStartYear + 1, 2, 31); // Mar 31 next
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  return { from: toYMD(start), to: toYMD(end) };
}

export default function ProductsTab() {
  const { id } = useParams(); // customer id
  const token = localStorage.getItem("token");

  const fy = useMemo(getCurrentFYRange, []);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    page: 1,
    pageSize: 25,
    totalRows: 0,
    totals: { qty: 0, value: 0, free_qty: 0 },
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const [from, setFrom] = useState(fy.from); // default to current FY
  const [to, setTo] = useState(fy.to);
  const [sort, setSort] = useState("total_value");
  const [dir, setDir] = useState("desc");

  const pageCount = useMemo(
    () =>
      Math.max(
        Math.ceil((meta.totalRows || 0) / (meta.pageSize || 25)),
        1
      ),
    [meta]
  );

  const fetchData = async (page = meta.page, pageSize = meta.pageSize) => {
    try {
      setLoading(true);
      setErr("");
      const res = await axios.get(
        `${API_BASE_URL}/api/customers/${id}/products`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { page, pageSize, search, from, to, sort, dir },
        }
      );
      setRows(res.data?.rows || []);
      setMeta(
        res.data?.meta || {
          page,
          pageSize,
          totalRows: 0,
          totals: { qty: 0, value: 0, free_qty: 0 },
        }
      );
    } catch (e) {
      console.error(e);
      setErr("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, meta.pageSize); // reset to page 1 on filter/sort change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, from, to, sort, dir, id]);

  const onChangeSort = (col) => {
    if (sort === col) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(col);
      setDir("desc");
    }
  };

  const resetFY = () => {
    setFrom(fy.from);
    setTo(fy.to);
  };

  const downloadCSV = () => {
    const header = ["Product", "Total Qty", "Free Qty", "Total Value", "Last Sold", "Last Invoice #"];
    const body = rows.map((r) => [
      `"${(r.name || "").replace(/"/g, '""')}"`,
      r.total_qty,
      r.total_free_qty,
      r.total_value,
      fmtDate(r.last_sold),
      r.last_invoice_no || "-",
    ]);
    const csv = [header.join(","), ...body.map((a) => a.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customer_${id}_products.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* ===== Top bar: filters & summary ===== */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        {/* Filters */}
        <div className="w-full md:w-auto">
          {/* Mobile-friendly stacked filters */}
          <div className="bg-white rounded-xl shadow p-3 flex flex-col gap-3">
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2">
              <FiSearch className="opacity-70" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search product..."
                className="outline-none w-full md:w-56"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={from}
                  max={to}
                  onChange={(e) => setFrom(e.target.value)}
                  className="border rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={to}
                  min={from}
                  onChange={(e) => setTo(e.target.value)}
                  className="border rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => fetchData(1, meta.pageSize)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-gray-50"
                title="Refresh"
              >
                <FiRefreshCw />
                Refresh
              </button>
              <button
                onClick={downloadCSV}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-gray-50"
                title="Download CSV"
              >
                <FiDownload />
                Export
              </button>
              <button
                onClick={resetFY}
                className="ml-auto px-3 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                title={`Reset to FY ${fy.from} → ${fy.to}`}
              >
                Current FY
              </button>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="text-right">
          <div className="text-xs uppercase opacity-60">Total Value</div>
          <div className="text-xl font-semibold">
            {fmtINR(meta?.totals?.value || 0)}
          </div>
          <div className="text-xs opacity-70">
            {meta.totalRows || 0} products •{" "}
            {Number(meta?.totals?.qty || 0).toLocaleString("en-IN")} units
            billed •{" "}
            {Number(meta?.totals?.free_qty || 0).toLocaleString("en-IN")} units
            free
          </div>
        </div>
      </div>

      {/* ===== MOBILE: cards ===== */}
      <div className="md:hidden space-y-3">
        {loading && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-center text-slate-500">
            Loading…
          </div>
        )}
        {!loading && err && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-center text-rose-700">
            {err}
          </div>
        )}
        {!loading && !err && rows.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-center text-slate-500">
            No products found
          </div>
        )}

        {!loading &&
          !err &&
          rows.map((r, idx) => (
            <div
              key={r.product_id}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-base font-semibold text-slate-900">
                    {r.name}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                      #{(meta.page - 1) * meta.pageSize + idx + 1}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                      {fmtINR(r.total_value)}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase opacity-60">Qty</div>
                  <div className="text-sm font-semibold">
                    {Number(r.total_qty || 0).toLocaleString("en-IN")}
                    {Number(r.total_free_qty || 0) > 0 ? (
                      <span className="ml-1 text-xs text-gray-500">
                        (+{Number(r.total_free_qty || 0).toLocaleString("en-IN")} free)
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-500">Last Sold</div>
                <div className="text-right">{fmtDate(r.last_sold)}</div>
                <div className="text-gray-500">Last Invoice #</div>
                <div className="text-right">{r.last_invoice_no || "-"}</div>
              </div>
            </div>
          ))}

        {/* Mobile pagination */}
        <div className="flex items-center justify-between mt-2">
          <div className="text-sm opacity-70">
            Page {meta.page} / {pageCount}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 rounded-lg border disabled:opacity-50"
              disabled={meta.page <= 1}
              onClick={() => fetchData(meta.page - 1, meta.pageSize)}
            >
              Prev
            </button>
            <button
              className="px-3 py-1 rounded-lg border disabled:opacity-50"
              disabled={meta.page >= pageCount}
              onClick={() => fetchData(meta.page + 1, meta.pageSize)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* ===== DESKTOP/TABLET: table ===== */}
      <div className="hidden md:block border rounded-xl overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr className="text-left">
              <th className="px-4 py-3 w-16">#</th>

              <th
                className="px-4 py-3 cursor-pointer select-none"
                onClick={() => onChangeSort("name")}
                title="Sort by product name"
              >
                Product
                {sort === "name" ? (
                  dir === "asc" ? (
                    <FiArrowUp className="inline ml-1" />
                  ) : (
                    <FiArrowDown className="inline ml-1" />
                  )
                ) : null}
              </th>

              <th
                className="px-4 py-3 text-right cursor-pointer select-none"
                onClick={() => onChangeSort("total_qty")}
                title="Sort by quantity"
              >
                Total Qty
                {sort === "total_qty" ? (
                  dir === "asc" ? (
                    <FiArrowUp className="inline ml-1" />
                  ) : (
                    <FiArrowDown className="inline ml-1" />
                  )
                ) : null}
              </th>

              <th className="px-4 py-3 text-right">Free Qty</th>

              <th
                className="px-4 py-3 text-right cursor-pointer select-none"
                onClick={() => onChangeSort("total_value")}
                title="Sort by value"
              >
                Total Value
                {sort === "total_value" ? (
                  dir === "asc" ? (
                    <FiArrowUp className="inline ml-1" />
                  ) : (
                    <FiArrowDown className="inline ml-1" />
                  )
                ) : null}
              </th>

              <th
                className="px-4 py-3 cursor-pointer select-none"
                onClick={() => onChangeSort("last_sold")}
                title="Sort by last sold date"
              >
                Last Sold
                {sort === "last_sold" ? (
                  dir === "asc" ? (
                    <FiArrowUp className="inline ml-1" />
                  ) : (
                    <FiArrowDown className="inline ml-1" />
                  )
                ) : null}
              </th>

              <th className="px-4 py-3">Last Invoice #</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : err ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-red-600">
                  {err}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  No products found
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={r.product_id} className="border-t">
                  <td className="px-4 py-3">
                    {(meta.page - 1) * meta.pageSize + idx + 1}
                  </td>
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3 text-right">
                    {Number(r.total_qty || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {Number(r.total_free_qty || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-3 text-right">{fmtINR(r.total_value)}</td>
                  <td className="px-4 py-3">{fmtDate(r.last_sold)}</td>
                  <td className="px-4 py-3">{r.last_invoice_no || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ===== Desktop pagination ===== */}
      <div className="hidden md:flex items-center justify-between mt-4">
        <div className="text-sm opacity-70">
          Page {meta.page} / {pageCount}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 rounded-lg border disabled:opacity-50"
            disabled={meta.page <= 1}
            onClick={() => fetchData(meta.page - 1, meta.pageSize)}
          >
            Prev
          </button>
          <button
            className="px-3 py-1 rounded-lg border disabled:opacity-50"
            disabled={meta.page >= pageCount}
            onClick={() => fetchData(meta.page + 1, meta.pageSize)}
          >
            Next
          </button>
          <select
            className="ml-2 border rounded-lg px-2 py-1"
            value={meta.pageSize}
            onChange={(e) => fetchData(1, Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
